import type { MessageInstance, AdapterStats, AdapterStatus } from '@k-ucap/shared';
import { getGlobalVirtualMessageAdapter } from './VirtualMessageAdapter.js';
import type { MessageTransportAdapter } from './MessageTransportAdapter.js';

export class ReplayAdapter implements MessageTransportAdapter {
  private messages: MessageInstance[] = [];
  private index = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private speed = 1;
  private startTime = 0;
  private virtualAdapter = getGlobalVirtualMessageAdapter();
  private stats: AdapterStats = {
    rxCount: 0,
    txCount: 0,
    rxBytes: 0,
    txBytes: 0,
    errors: 0,
    reconnects: 0,
  };
  private statusHandlers: Set<(status: AdapterStatus, error?: string) => void> = new Set();

  getName(): string {
    return 'ReplayAdapter';
  }

  async connect(): Promise<void> {
    // no-op: replay uses the global virtual adapter
  }

  async disconnect(): Promise<void> {
    this.stop();
  }

  async publish(): Promise<void> {
    // no-op: replay does not accept inbound publish
  }

  subscribe(): () => void {
    return () => {};
  }

  getStatus(): AdapterStatus {
    return 'connected';
  }

  getStats(): AdapterStats {
    return { ...this.stats };
  }

  onStatusChange(callback: (status: AdapterStatus, error?: string) => void): () => void {
    this.statusHandlers.add(callback);
    return () => this.statusHandlers.delete(callback);
  }

  loadMessages(messages: MessageInstance[]): void {
    this.messages = messages;
    this.index = 0;
  }

  setSpeed(speed: number): void {
    this.speed = Math.max(0.1, speed);
  }

  play(): void {
    if (this.timer) clearInterval(this.timer);
    if (this.messages.length === 0) return;
    const startTs = new Date(this.messages[0].timestamp).getTime();
    this.startTime = performance.now();
    this.timer = setInterval(() => {
      const elapsed = (performance.now() - this.startTime) * this.speed;
      const currentTs = startTs + elapsed;
      while (this.index < this.messages.length) {
        const msg = this.messages[this.index];
        const msgTs = new Date(msg.timestamp).getTime();
        if (msgTs <= currentTs) {
          this.virtualAdapter.publish(msg).catch(() => {});
          this.stats.txCount++;
          this.stats.txBytes += JSON.stringify(msg).length;
          this.index++;
        } else {
          break;
        }
      }
      if (this.index >= this.messages.length) {
        this.pause();
      }
    }, 16);
  }

  pause(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  stop(): void {
    this.pause();
    this.index = 0;
  }

  getProgress(): { current: number; total: number } {
    return { current: this.index, total: this.messages.length };
  }
}

export const createReplayAdapter = (): ReplayAdapter => new ReplayAdapter();
